type RolePrefixFunc<T = any> = (rolePrefix: 'user' | 'admin' | 'partneradmin' | 'superadmin') => T;

// --------------
// ROUTING PREFIX
// --------------

const ADMIN_ROUTE_PREFIX: RolePrefixFunc = rolePrefix => `/${rolePrefix}/client/:entityId`;
const PARTNERADMIN_ROUTE_PREFIX = '/partneradmin/:entityId';

// --------------
// AUTH NAMESPACE
// --------------

const BASE = {
  ROOT: '/',
  SIGN_IN: '/',
  CLOSE: '/close',
  SIGN_UP: '/sign-up',
  SIGN_UP_COMPANY: '/sign-up/company',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/change-password',
  SET_PASSWORD: '/set-password',
  VERIFICATION: '/verification/:invitationId',
  FIRM_VERIFICATION: '/firm-verification/:invitationId',
  FIRMADMIN_VERIFICATION: '/firmadmin-verification',
  TEST_ASSIGNMENT: '/test/:assignmentToken',
  // `:assignmentId` is an enrollment id; sent e-mails carry this link, so the path and param stay.
  TRAINING_ASSIGNMENT_LINK: '/training-assignments/:assignmentId',
  TRAINING_CATALOG_LINK: '/trainings/catalog',
  GW_IMPORT: '/gwimport',
  OTP_SETUP: '/otp-setup',
  MDM_ENROLL: '/mdm/apple/enroll',
  MDM_ANDROID_CALLBACK: '/mdm/android/callback'
};

// Tolerant match for the Apple MDM enrollment path: Apple/Monarch controls the
// redirect target, so accept trailing slashes and case variance rather than
// silently 404-ing on `/mdm/apple/enroll/` or a differently-cased path.
export const isMdmEnrollPath = (pathname: string): boolean =>
  pathname.replace(/\/+$/, '').toLowerCase() === BASE.MDM_ENROLL;

// Where Google returns the admin after binding the company's Google account. The
// path is configured on the backend and cannot carry the client id, so it is
// fixed here and matched just as tolerantly as the Apple one.
export const isMdmAndroidCallbackPath = (pathname: string): boolean =>
  pathname.replace(/\/+$/, '').toLowerCase() === BASE.MDM_ANDROID_CALLBACK;

// --------------
// DASHBOARD NAMESPACE
// --------------

const SUPERADMIN_DASHBOARD = '/dashboard';
const PARTNERADMIN_DASHBOARD = `${PARTNERADMIN_ROUTE_PREFIX}/dashboard`;
const DASHBOARD: RolePrefixFunc = rolePrefix => `${ADMIN_ROUTE_PREFIX(rolePrefix)}/dashboard`;

// --------------
// DEVICES NAMESPACE
// --------------

const DEVICES_NAMESPACE: RolePrefixFunc = rolePrefix => `${ADMIN_ROUTE_PREFIX(rolePrefix)}/devices`;
const DEVICES: RolePrefixFunc<{
  ROOT: string;
  LIST: string;
  DETAIL: string;
  REMOTE_SESSION: string;
  IMPORT: string;
  IMPORT_DETAIL: string;
  TRANSFER: string;
}> = rolePrefix => ({
  ROOT: DEVICES_NAMESPACE(rolePrefix),
  LIST: DEVICES_NAMESPACE(rolePrefix),
  DETAIL: `${DEVICES_NAMESPACE(rolePrefix)}/:platform/:id`,
  // Opened in a window of its own, so it deliberately renders without the admin
  // layout - a remote screen wants the whole viewport and no sidebar.
  REMOTE_SESSION: `${DEVICES_NAMESPACE(rolePrefix)}/:platform/:id/remote`,
  IMPORT: `${DEVICES_NAMESPACE(rolePrefix)}/import`,
  IMPORT_DETAIL: `${DEVICES_NAMESPACE(rolePrefix)}/import/:headlessTokenId`,
  TRANSFER: `${DEVICES_NAMESPACE(rolePrefix)}/transfer`
});

// --------------
// REMOTE DESKTOP NAMESPACE
// --------------

const REMOTE_DESKTOP: RolePrefixFunc = rolePrefix => `${ADMIN_ROUTE_PREFIX(rolePrefix)}/remote-desktop`;

// --------------
// ISSUES NAMESPACE
// --------------

const ISSUES_NAMESPACE: RolePrefixFunc = rolePrefix => `${ADMIN_ROUTE_PREFIX(rolePrefix)}/issues`;
const ISSUES: RolePrefixFunc<{
  ROOT: string;
  LIST: string;
}> = rolePrefix => ({
  ROOT: ISSUES_NAMESPACE(rolePrefix),
  LIST: ISSUES_NAMESPACE(rolePrefix)
});

// --------------
// NETWORKS NAMESPACE
// --------------

const NETWORKS_NAMESPACE: RolePrefixFunc = rolePrefix => `${ADMIN_ROUTE_PREFIX(rolePrefix)}/networks`;
const NETWORKS: RolePrefixFunc<{
  ROOT: string;
  LIST: string;
  DETAIL: string;
}> = rolePrefix => ({
  ROOT: NETWORKS_NAMESPACE(rolePrefix),
  LIST: NETWORKS_NAMESPACE(rolePrefix),
  DETAIL: `${NETWORKS_NAMESPACE(rolePrefix)}/:type/:id`
});

// --------------
// MONITORING NAMESPACE
// --------------

const MONITORING_NAMESPACE: RolePrefixFunc = rolePrefix => `${ADMIN_ROUTE_PREFIX(rolePrefix)}/monitoring`;
const MONITORING: RolePrefixFunc<{
  SSL_MONITOR: string;
  PUBLIC_PORT_SCAN: string;
}> = rolePrefix => ({
  SSL_MONITOR: `${MONITORING_NAMESPACE(rolePrefix)}/ssl-monitor`,
  PUBLIC_PORT_SCAN: `${MONITORING_NAMESPACE(rolePrefix)}/public-port-scan`
});

// --------------
// SETTINGS NAMESPACE
// --------------

const SUPERADMIN_SETTINGS = '/settings';
const PARTNERADMIN_SETTINGS = `${PARTNERADMIN_ROUTE_PREFIX}/settings`;
const ACCOUNT_SETTINGS: RolePrefixFunc = rolePrefix => `${ADMIN_ROUTE_PREFIX(rolePrefix)}/account/settings`;
const GLOBAL_SETTINGS: RolePrefixFunc = rolePrefix => `${ADMIN_ROUTE_PREFIX(rolePrefix)}/global/settings`;

// --------------
// MDM NAMESPACE (client admin)
// --------------

const MDM_NAMESPACE: RolePrefixFunc = rolePrefix => `${ADMIN_ROUTE_PREFIX(rolePrefix)}/mdm`;
const MDM: RolePrefixFunc<{
  LIST: string;
}> = rolePrefix => ({
  LIST: MDM_NAMESPACE(rolePrefix)
});

// --------------
// NOTIFICATIONS NAMESPACE
// --------------

const NOTIFICATIONS_NAMESPACE: RolePrefixFunc = rolePrefix => `${ADMIN_ROUTE_PREFIX(rolePrefix)}/notifications`;
const NOTIFICATIONS: RolePrefixFunc<{
  ROOT: string;
  LIST: string;
  ADD: string;
  NOTICE: string;
}> = rolePrefix => ({
  ROOT: NOTIFICATIONS_NAMESPACE(rolePrefix),
  LIST: NOTIFICATIONS_NAMESPACE(rolePrefix),
  ADD: `${NOTIFICATIONS_NAMESPACE(rolePrefix)}/add`,
  NOTICE: `${NOTIFICATIONS_NAMESPACE(rolePrefix)}/notice/:type/:id`
});

// --------------
// USERS NAMESPACE
// --------------

const USERS_NAMESPACE: RolePrefixFunc = rolePrefix => `${ADMIN_ROUTE_PREFIX(rolePrefix)}/users`;
const USERS: RolePrefixFunc<{
  ROOT: string;
  LIST: string;
  DETAIL: string;
  ADD: string;
}> = rolePrefix => ({
  ROOT: USERS_NAMESPACE(rolePrefix),
  LIST: USERS_NAMESPACE(rolePrefix),
  DETAIL: `${USERS_NAMESPACE(rolePrefix)}/detail/:id`,
  ADD: `${USERS_NAMESPACE(rolePrefix)}/add`
});

// --------------
// CUSTOMERS NAMESPACE
// --------------
const CUSTOMERS_NAMESPACE = `${PARTNERADMIN_ROUTE_PREFIX}/customers`;
const CUSTOMERS = {
  ROOT: CUSTOMERS_NAMESPACE,
  LIST: CUSTOMERS_NAMESPACE,
  ADD: `${CUSTOMERS_NAMESPACE}/add`,
  DETAIL: `${CUSTOMERS_NAMESPACE}/:id`
};

// --------------
// PARTNERS NAMESPACE
// --------------
const PARTNERS_NAMESPACE = '/partners';
const PARTNERS = {
  ROOT: PARTNERS_NAMESPACE,
  LIST: PARTNERS_NAMESPACE,
  ADD: `${PARTNERS_NAMESPACE}/add`,
  DETAIL: `${PARTNERS_NAMESPACE}/:id`
};

// --------------
// COMPLIANCE NAMESPACE
// --------------
const ADMIN_COMPLIANCE_NAMESPACE: RolePrefixFunc = rolePrefix => `${ADMIN_ROUTE_PREFIX(rolePrefix)}/compliance`;
const ADMIN_COMPLIANCE: RolePrefixFunc<{
  ROOT: string;
  LIST: string;
  ADD: string;
  NIS2_ADD: string;
  NIS2_DETAIL: string;
  CUSTOM_DETAIL: string;
}> = rolePrefix => ({
  ROOT: ADMIN_COMPLIANCE_NAMESPACE(rolePrefix),
  LIST: ADMIN_COMPLIANCE_NAMESPACE(rolePrefix),
  ADD: `${ADMIN_COMPLIANCE_NAMESPACE(rolePrefix)}/add`,
  NIS2_ADD: `${ADMIN_COMPLIANCE_NAMESPACE(rolePrefix)}/add/nis2`,
  NIS2_DETAIL: `${ADMIN_COMPLIANCE_NAMESPACE(rolePrefix)}/detail/nis2`,
  CUSTOM_DETAIL: `${ADMIN_COMPLIANCE_NAMESPACE(rolePrefix)}/detail/custom/:id`
});

const PARTNERADMIN_COMPLIANCE_NAMESPACE = `${PARTNERADMIN_ROUTE_PREFIX}/compliance`;
const PARTNERADMIN_COMPLIANCE: {
  ROOT: string;
  LIST: string;
  CUSTOM_DETAIL: string;
} = {
  ROOT: PARTNERADMIN_COMPLIANCE_NAMESPACE,
  LIST: PARTNERADMIN_COMPLIANCE_NAMESPACE,
  CUSTOM_DETAIL: `${PARTNERADMIN_COMPLIANCE_NAMESPACE}/detail/custom/:id`
};

// --------------
// REGULATIONS NAMESPACE
// --------------
const REGULATIONS_NAMESPACE: RolePrefixFunc = rolePrefix => `${ADMIN_ROUTE_PREFIX(rolePrefix)}/regulations`;
const REGULATIONS: RolePrefixFunc<{
  ROOT: string;
  DASHBOARD: string;
  GETTING_STARTED_DETAIL: string;
  GETTING_STARTED_LIST: string;
  ASSETS: string;
  ASSETS_REVIEW: string;
  ASSET_IMPORT: string;
  SERVICES: string;
  SERVICE_DETAIL: string;
  SERVICE_REGULATION: string;
  SERVICE_REGULATION_ADD_ASSETS: string;
  REGULATION_LIST: string;
  REGULATION_DETAIL: string;
  REGULATION_CREATE: string;
  REGULATION_EDIT: string;
  SUPPLIERS: string;
  SUPPLIER_DETAIL: string;
  AUDIT_LOG: string;
}> = rolePrefix => ({
  ROOT: REGULATIONS_NAMESPACE(rolePrefix),
  DASHBOARD: REGULATIONS_NAMESPACE(rolePrefix),
  GETTING_STARTED_DETAIL: `${REGULATIONS_NAMESPACE(rolePrefix)}/getting-started/:regulationCodename`,
  GETTING_STARTED_LIST: `${REGULATIONS_NAMESPACE(rolePrefix)}/getting-started`,
  ASSETS: `${REGULATIONS_NAMESPACE(rolePrefix)}/assets/:assetId?`,
  ASSETS_REVIEW: `${REGULATIONS_NAMESPACE(rolePrefix)}/assets/review`,
  ASSET_IMPORT: `${REGULATIONS_NAMESPACE(rolePrefix)}/assets/import`,
  SERVICES: `${REGULATIONS_NAMESPACE(rolePrefix)}/services/`,
  SERVICE_DETAIL: `${REGULATIONS_NAMESPACE(rolePrefix)}/services/:serviceId`,
  SERVICE_REGULATION: `${REGULATIONS_NAMESPACE(rolePrefix)}/services/:serviceId/:regulationCodename/:assetId?`,
  SERVICE_REGULATION_ADD_ASSETS: `${REGULATIONS_NAMESPACE(rolePrefix)}/services/:serviceId/:regulationCodename/add-assets`,
  REGULATION_LIST: `${REGULATIONS_NAMESPACE(rolePrefix)}/list`,
  REGULATION_DETAIL: `${REGULATIONS_NAMESPACE(rolePrefix)}/detail/:regulationCodename`,
  REGULATION_CREATE: `${REGULATIONS_NAMESPACE(rolePrefix)}/list/create`,
  REGULATION_EDIT: `${REGULATIONS_NAMESPACE(rolePrefix)}/detail/:regulationCodename/edit`,
  SUPPLIERS: `${REGULATIONS_NAMESPACE(rolePrefix)}/suppliers/`,
  SUPPLIER_DETAIL: `${REGULATIONS_NAMESPACE(rolePrefix)}/suppliers/:supplierId`,
  AUDIT_LOG: `${REGULATIONS_NAMESPACE(rolePrefix)}/audit-log`
});

// --------------
// EVENT LOG NAMESPACE
// --------------
const EVENT_LOG: RolePrefixFunc = rolePrefix => `${ADMIN_ROUTE_PREFIX(rolePrefix)}/event-log`;

// --------------
// PRIVACY-PROTECTION NAMESPACE
// --------------
const PRIVACY_PROTECTION: RolePrefixFunc = rolePrefix => `${ADMIN_ROUTE_PREFIX(rolePrefix)}/privacy-protection`;

// --------------
// EDUCATION NAMESPACE
// --------------
const EDUCATION_NAMESPACE: RolePrefixFunc = rolePrefix => `${ADMIN_ROUTE_PREFIX(rolePrefix)}/education`;
const EDUCATION: RolePrefixFunc<{
  ROOT: string;
  TESTS: string;
  CREATE_TEST: string;
  CREATE_TEST_TEMPLATE: string;
  RECOMMENDATIONS: string;
  RECOMMENDATIONS_NOTIFICATION: string;
  RECOMMENDATIONS_CREATE_NOTICE: string;
  LOCAL_THREATS_LIST: string;
  LOCAL_THREATS_NOTIFICATION: string;
  LOCAL_THREATS_CREATE_NOTICE: string;
  PHISHING_SIMULATIONS: string;
  PHISHING_SIMULATIONS_DETAIL: string;
  PHISHING_SIMULATIONS_ADD: string;
  PHISHING_LIBRARY: string;
  TRAININGS: string;
  TRAINING_ASSIGN: string;
  TRAINING_ASSIGNMENT_DETAIL: string;
  TRAINING_PARTICIPANT: string;
  TRAINING_CATALOG: string;
  TRAINING_CATALOG_DETAIL: string;
  TRAINING_EDITOR: string;
  TRAINING_COMPLIANCE: string;
  TRAINING_TEST_LIBRARY: string;
  TRAINING_TEST_EDITOR: string;
  TRAINING_ENROLLMENT: string;
  TRAINING_ATTEMPT: string;
}> = rolePrefix => ({
  ROOT: EDUCATION_NAMESPACE(rolePrefix),
  TESTS: `${EDUCATION_NAMESPACE(rolePrefix)}/tests`,
  CREATE_TEST: `${EDUCATION_NAMESPACE(rolePrefix)}/create-test`,
  CREATE_TEST_TEMPLATE: `${EDUCATION_NAMESPACE(rolePrefix)}/create-test-template/:templateId?`,
  RECOMMENDATIONS: `${EDUCATION_NAMESPACE(rolePrefix)}/recommendations`,
  RECOMMENDATIONS_NOTIFICATION: `${EDUCATION_NAMESPACE(rolePrefix)}/recommendations/notification/:id`,
  RECOMMENDATIONS_CREATE_NOTICE: `${EDUCATION_NAMESPACE(rolePrefix)}/recommendations/create/:noticeId?`,
  LOCAL_THREATS_LIST: `${EDUCATION_NAMESPACE(rolePrefix)}/local-threats`,
  LOCAL_THREATS_NOTIFICATION: `${EDUCATION_NAMESPACE(rolePrefix)}/local-threats/notification/:id`,
  LOCAL_THREATS_CREATE_NOTICE: `${EDUCATION_NAMESPACE(rolePrefix)}/local-threats/create/:noticeId?`,
  PHISHING_SIMULATIONS: `${EDUCATION_NAMESPACE(rolePrefix)}/phishing-simulations`,
  PHISHING_SIMULATIONS_DETAIL: `${EDUCATION_NAMESPACE(rolePrefix)}/phishing-simulations/detail/:id`,
  PHISHING_SIMULATIONS_ADD: `${EDUCATION_NAMESPACE(rolePrefix)}/phishing-simulations/add`,
  PHISHING_LIBRARY: `${EDUCATION_NAMESPACE(rolePrefix)}/phishing-simulations/library`,
  TRAININGS: `${EDUCATION_NAMESPACE(rolePrefix)}/trainings`,
  TRAINING_ASSIGN: `${EDUCATION_NAMESPACE(rolePrefix)}/trainings/assign/:assignmentId?`,
  TRAINING_ASSIGNMENT_DETAIL: `${EDUCATION_NAMESPACE(rolePrefix)}/trainings/assignments/:assignmentId`,
  TRAINING_PARTICIPANT: `${EDUCATION_NAMESPACE(rolePrefix)}/trainings/assignments/:assignmentId/participants/:enrollmentId`,
  TRAINING_CATALOG: `${EDUCATION_NAMESPACE(rolePrefix)}/trainings/catalog`,
  TRAINING_CATALOG_DETAIL: `${EDUCATION_NAMESPACE(rolePrefix)}/trainings/catalog/:trainingId`,
  TRAINING_EDITOR: `${EDUCATION_NAMESPACE(rolePrefix)}/trainings/editor/:trainingId?`,
  TRAINING_COMPLIANCE: `${EDUCATION_NAMESPACE(rolePrefix)}/trainings/compliance`,
  TRAINING_TEST_LIBRARY: `${EDUCATION_NAMESPACE(rolePrefix)}/trainings/library`,
  TRAINING_TEST_EDITOR: `${EDUCATION_NAMESPACE(rolePrefix)}/trainings/library/edit/:templateId?`,
  TRAINING_ENROLLMENT: `${EDUCATION_NAMESPACE(rolePrefix)}/trainings/:enrollmentId`,
  TRAINING_ATTEMPT: `${EDUCATION_NAMESPACE(rolePrefix)}/trainings/:enrollmentId/attempts/:attemptId`
});

export default {
  BASE,
  USER: {
    DASHBOARD: DASHBOARD('user'),
    DEVICES: DEVICES('user'),
    ISSUES: ISSUES('user'),
    NETWORKS: NETWORKS('user'),
    NOTIFICATIONS: NOTIFICATIONS('user'),
    ACCOUNT_SETTINGS: ACCOUNT_SETTINGS('user'),
    PRIVACY_PROTECTION: PRIVACY_PROTECTION('user'),
    EDUCATION: EDUCATION('user')
  },
  ADMIN: {
    DASHBOARD: DASHBOARD('admin'),
    DEVICES: DEVICES('admin'),
    REMOTE_DESKTOP: REMOTE_DESKTOP('admin'),
    ACCOUNT_SETTINGS: ACCOUNT_SETTINGS('admin'),
    GLOBAL_SETTINGS: GLOBAL_SETTINGS('admin'),
    ISSUES: ISSUES('admin'),
    NETWORKS: NETWORKS('admin'),
    MONITORING: MONITORING('admin'),
    USERS: USERS('admin'),
    NOTIFICATIONS: NOTIFICATIONS('admin'),
    PRIVACY_PROTECTION: PRIVACY_PROTECTION('admin'),
    EDUCATION: EDUCATION('admin'),
    COMPLIANCE: ADMIN_COMPLIANCE('admin'),
    REGULATIONS: REGULATIONS('admin'),
    EVENT_LOG: EVENT_LOG('admin'),
    OS_PATCHES: `${ADMIN_ROUTE_PREFIX('admin')}/os-patches`,
    APPS: `${ADMIN_ROUTE_PREFIX('admin')}/apps`,
    REMOVABLE_MEDIA: `${ADMIN_ROUTE_PREFIX('admin')}/removable-media`,
    MDM: MDM('admin')
  },
  FIRMADMIN: {
    DASHBOARD: PARTNERADMIN_DASHBOARD,
    DEVICES: `${PARTNERADMIN_ROUTE_PREFIX}/devices`,
    NETWORKS: `${PARTNERADMIN_ROUTE_PREFIX}/networks`,
    PRIVACY_PROTECTION: `${PARTNERADMIN_ROUTE_PREFIX}/privacy-protection`,
    ISSUES: `${PARTNERADMIN_ROUTE_PREFIX}/issues`,
    EDUCATION: {
      TESTS: `${PARTNERADMIN_ROUTE_PREFIX}/education/tests`,
      CREATE_TEST_TEMPLATE: `${PARTNERADMIN_ROUTE_PREFIX}/education/create-test-template/:templateId?`,
      RECOMMENDATIONS: `${PARTNERADMIN_ROUTE_PREFIX}/recommendations`,
      RECOMMENDATIONS_NOTIFICATION: `${PARTNERADMIN_ROUTE_PREFIX}/recommendations/notification/:id`,
      RECOMMENDATIONS_CREATE_NOTICE: `${PARTNERADMIN_ROUTE_PREFIX}/recommendations/create/:noticeId?`,
      LOCAL_THREATS_LIST: `${PARTNERADMIN_ROUTE_PREFIX}/local-threats`,
      LOCAL_THREATS_NOTIFICATION: `${PARTNERADMIN_ROUTE_PREFIX}/local-threats/notification/:id`,
      LOCAL_THREATS_CREATE_NOTICE: `${PARTNERADMIN_ROUTE_PREFIX}/local-threats/create/:noticeId?`,
      TRAININGS: `${PARTNERADMIN_ROUTE_PREFIX}/trainings`,
      TRAINING_DETAIL: `${PARTNERADMIN_ROUTE_PREFIX}/trainings/:trainingId`,
      TRAINING_EDITOR: `${PARTNERADMIN_ROUTE_PREFIX}/trainings/editor/:trainingId?`,
      TRAINING_TEST_LIBRARY: `${PARTNERADMIN_ROUTE_PREFIX}/trainings/library`,
      TRAINING_TEST_EDITOR: `${PARTNERADMIN_ROUTE_PREFIX}/trainings/library/edit/:templateId?`,
      TRAINING_ACCESS: `${PARTNERADMIN_ROUTE_PREFIX}/training-access`
    },
    CUSTOMERS,
    SETTINGS: PARTNERADMIN_SETTINGS,
    ADMINS: {
      ROOT: `${PARTNERADMIN_ROUTE_PREFIX}/admins`,
      LIST: `${PARTNERADMIN_ROUTE_PREFIX}/admins`,
      DETAIL: `${PARTNERADMIN_ROUTE_PREFIX}/admins/detail/:id`,
      ADD: `${PARTNERADMIN_ROUTE_PREFIX}/admins/add`
    },
    COMPLIANCE: PARTNERADMIN_COMPLIANCE,
    GLOBAL_SETTINGS: GLOBAL_SETTINGS('partneradmin'),
    DOCUMENTS: `${PARTNERADMIN_ROUTE_PREFIX}/documents`,
    DOCUMENTS_REDIRECT: 'partner/documents'
  },
  SUPERADMIN: {
    DASHBOARD: SUPERADMIN_DASHBOARD,
    PARTNERS,
    DOCUMENTS: '/documents',
    APP_LOGS: '/app-logs',
    LICENSES: '/licenses',
    MDM: {
      LIST: '/mdm',
      NEW: '/mdm/new'
    },
    SETTINGS: SUPERADMIN_SETTINGS,
    GLOBAL_SETTINGS: GLOBAL_SETTINGS('admin'),
    EDUCATION: {
      RECOMMENDATIONS: '/recommendations',
      RECOMMENDATIONS_NOTIFICATION: '/recommendations/notification/:id',
      RECOMMENDATIONS_CREATE_NOTICE: '/recommendations/create/:noticeId?',
      RECOMMENDATIONS_CREATE_NOTIFICATION: '/recommendations/notification/:id',
      LOCAL_THREATS_LIST: '/local-threats',
      LOCAL_THREATS_NOTIFICATION: '/local-threats/notification/:id',
      LOCAL_THREATS_CREATE_NOTICE: '/local-threats/create/:noticeId?',
      LOCAL_THREATS_CREATE_NOTIFICATION: '/local-threats/notification/:id',
      PHISHING_LIBRARY: '/phishing-library',
      QUIZZES: '/quizzes',
      CREATE_TEST_TEMPLATE: '/quizzes/create-template/:templateId?',
      TRAININGS: '/trainings',
      TRAINING_DETAIL: '/trainings/:trainingId',
      TRAINING_EDITOR: '/trainings/editor/:trainingId?',
      TRAINING_TEST_LIBRARY: '/trainings/library',
      TRAINING_TEST_EDITOR: '/trainings/library/edit/:templateId?',
      TRAINING_ACCESS: '/training-access'
    }
  }
};
